import React, { useState, useEffect, useMemo } from 'react';
import { items } from './QuoteOfTheDayData';

// Helper component for icons
const Icon = ({ path, className = 'w-6 h-6' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

// Individual Quote Card Component
const QuoteCard = ({ quote, isExpanded, onToggleExpand }) => {
  const isLongQuote = quote.Question.length > 200;
  const displayQuote =
    isLongQuote && !isExpanded
      ? `${quote.Question.substring(0, 200)}...`
      : quote.Question;
  const sourceUrl = `https://baws.in${quote.Response[0]?.Answer}`;

  return (
    <div className="bg-white/70 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl shadow-lg p-6 md:p-8 w-full transition-all duration-300 ease-in-out transform hover:scale-[1.02]">
      <blockquote className="relative">
        <p className="text-lg md:text-xl font-medium text-gray-800 dark:text-gray-200 leading-relaxed">
          "{displayQuote}"
        </p>
      </blockquote>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        {isLongQuote && (
          <button
            onClick={onToggleExpand}
            className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline focus:outline-none mb-3 sm:mb-0"
          >
            {isExpanded ? 'Show Less' : 'Read More'}
          </button>
        )}
        {quote.Response[0]?.Answer && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors duration-200"
          >
            <Icon
              path="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
              className="w-4 h-4 mr-2"
            />
            Source
          </a>
        )}
      </div>
    </div>
  );
};

// Main Component - now named for easier import
export default function AmbedkarQuoteBrowser() {
  const [quotes, setQuotes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // In a real app, you would fetch this from a file or an API.
  // For now, it's hardcoded.
  const quoteData = items;

  useEffect(() => {
    try {
      // Simulating a fetch call
      setQuotes(quoteData);
      setCurrentIndex(0);
      setIsLoading(false);
    } catch (err) {
      setError('Failed to load quotes.');
      setIsLoading(false);
    }
  }, [quoteData]);

  const handleNext = () => {
    setIsExpanded(false);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % quotes.length);
  };

  const handlePrev = () => {
    setIsExpanded(false);
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + quotes.length) % quotes.length
    );
  };

  const handleShuffle = () => {
    setIsExpanded(false);
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * quotes.length);
    } while (quotes.length > 1 && newIndex === currentIndex);
    setCurrentIndex(newIndex);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (isLoading) {
    return <div className="text-center p-8">Loading Quotes...</div>;
  }

  if (error) {
    return <div className="text-center p-8 text-red-600">{error}</div>;
  }

  if (quotes.length === 0) {
    return <div className="text-center p-8">No quotes available.</div>;
  }

  return (
    <div className="w-full max-w-2xl mx-auto p-4 font-sans text-gray-800 dark:text-gray-200">
      <header className="text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-indigo-600 dark:from-sky-400 dark:to-indigo-500">
          Words of Dr. Ambedkar
        </h1>
        <p className="text-md text-gray-600 dark:text-gray-400 mt-2">
          An Inspiring Collection of Quotes
        </p>
      </header>

      <div className="relative w-full">
        <QuoteCard
          quote={quotes[currentIndex]}
          isExpanded={isExpanded}
          onToggleExpand={toggleExpand}
        />
      </div>

      <div className="flex items-center justify-center space-x-4 mt-8">
        <button
          onClick={handlePrev}
          aria-label="Previous Quote"
          className="p-3 rounded-full bg-white/80 dark:bg-gray-700/80 text-gray-800 dark:text-white shadow-md hover:bg-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900 transition-all duration-200"
        >
          <Icon path="M15.75 19.5L8.25 12l7.5-7.5" />
        </button>

        <button
          onClick={handleShuffle}
          aria-label="Shuffle Quote"
          className="px-6 py-3 rounded-full bg-gradient-to-r from-sky-500 to-indigo-600 text-white font-semibold shadow-lg hover:from-sky-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200"
        >
          Shuffle
        </button>

        <button
          onClick={handleNext}
          aria-label="Next Quote"
          className="p-3 rounded-full bg-white/80 dark:bg-gray-700/80 text-gray-800 dark:text-white shadow-md hover:bg-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900 transition-all duration-200"
        >
          <Icon path="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </button>
      </div>
    </div>
  );
}
